import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LogActivityDialog from "@/dealership/components/LogActivityDialog";
import DeskDealButton from "@/dealership/components/desk/DeskDealButton";
import { stageStyle, fmtDate } from "@/dealership/lib/funnel";
import { ArrowLeft, Pencil, Trash2, Phone, Mail, Car, DollarSign, User, Tag, Clock, StickyNote, Plus, ArrowRightLeft, Sparkles } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Negotiating", "Won", "Lost"];
const statusStyle = {
  New: "bg-blue-500/12 text-blue-400", Contacted: "bg-indigo-500/12 text-indigo-400",
  Qualified: "bg-amber-500/12 text-amber-400", Negotiating: "bg-purple-500/12 text-purple-400",
  Won: "bg-green-500/12 text-green-400", Lost: "bg-rose-500/12 text-rose-400",
};
const fmt = (d) => { if (!d) return ""; try { return new Date(d).toLocaleString(); } catch { return d; } };

function Field({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value}</p>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);

  const { data: lead, isLoading, isError } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["activities", id],
    queryFn: async () => (await api.get("/activities", { params: { lead_id: id } })).data,
    enabled: !!lead,
  });

  const statusMut = useMutation({
    mutationFn: async (status) => (await api.put(`/leads/${id}`, { ...lead, status, budget: lead.budget ?? null })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Status updated"); },
    onError: () => toast.error("Failed to update status"),
  });
  const delMut = useMutation({
    mutationFn: async () => api.delete(`/leads/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead deleted"); navigate("/dealership/leads"); },
  });
  const convertMut = useMutation({
    mutationFn: async () => (await api.post(`/leads/${id}/convert`)).data,
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Lead converted to a deal — opening it for review.");
      navigate(`/dealership/deals?edit=${deal.id}`);
    },
    onError: () => toast.error("Failed to convert lead"),
  });

  const sendAiReplyMut = useMutation({
    mutationFn: async () => (await api.post(`/leads/${id}/send-auto-reply`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(`AI reply sent to ${d.to}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send AI reply"),
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (isError || !lead) return <div className="p-8 text-muted-foreground">Lead not found. <button className="text-primary underline" onClick={() => navigate("/dealership/leads")}>Back to Leads</button></div>;

  const noteLines = (lead.notes || "").split(/\s*\|\s*/).map((s) => s.trim()).filter(Boolean);

  return (
    <div className="p-8 max-w-5xl" data-testid="lead-detail-page">
      <button onClick={() => navigate("/dealership/leads")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="lead-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Leads
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl">{lead.name}</h1>
            <Badge className={`${statusStyle[lead.status]} border-0`} data-testid="lead-detail-status-badge">{lead.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{lead.vehicle_interest || "No vehicle interest noted"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={lead.status} onValueChange={(v) => statusMut.mutate(v)}>
            <SelectTrigger className="w-40" data-testid="lead-detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setLogOpen(true)} data-testid="lead-detail-log-activity"><Plus className="h-4 w-4 mr-2" />Log Activity</Button>
          <DeskDealButton leadId={id} label="Desk a deal" testid="lead-detail-desk" />
          {lead.email && !lead.auto_replied && (
            <Button
              variant="outline"
              onClick={() => { if (window.confirm(`Send the AI auto-reply email now to ${lead.email}?`)) sendAiReplyMut.mutate(); }}
              disabled={sendAiReplyMut.isPending}
              data-testid="lead-detail-send-ai-reply"
              title="Send the standard CarGurus auto-reply email to this lead now"
            ><Sparkles className="h-4 w-4 mr-2" />{sendAiReplyMut.isPending ? "Sending…" : "Send AI reply now"}</Button>
          )}
          {lead.auto_replied && (
            <Badge className="bg-emerald-500/12 text-emerald-400 border-0 h-9 px-3 flex items-center gap-1.5" data-testid="lead-detail-replied-badge" title={lead.auto_reply_at ? `Sent ${fmt(lead.auto_reply_at)}` : ""}>
              <Sparkles className="h-3.5 w-3.5" /> AI reply sent
            </Badge>
          )}
          {lead.status !== "Won" && (
            <Button onClick={() => { if (window.confirm(`Convert "${lead.name}" into a deal? This marks the lead as Won.`)) convertMut.mutate(); }} disabled={convertMut.isPending} data-testid="lead-detail-convert"><ArrowRightLeft className="h-4 w-4 mr-2" />Convert to Deal</Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/dealership/leads?edit=${id}`)} data-testid="lead-detail-edit"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm("Delete this lead?")) delMut.mutate(); }} data-testid="lead-detail-delete"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-5 md:col-span-1">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Details</p>
          <Field icon={Phone} label="Phone" value={lead.phone} />
          <Field icon={Mail} label="Email" value={lead.email} />
          <Field icon={Car} label="Vehicle Interest" value={lead.vehicle_interest} />
          <Field icon={DollarSign} label="Budget" value={lead.budget != null ? `$${Number(lead.budget).toLocaleString()}` : ""} />
          <Field icon={Tag} label="Source" value={lead.source} />
          <Field icon={User} label="Assigned To" value={lead.assigned_to} />
          <Field icon={Clock} label="Added" value={fmt(lead.created_at)} />
          <Field icon={Clock} label="Last Updated" value={fmt(lead.updated_at)} />
        </Card>

        <div className="md:col-span-2 space-y-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3"><StickyNote className="h-4 w-4 text-muted-foreground" /><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Notes</p></div>
            {noteLines.length === 0 ? <p className="text-sm text-muted-foreground italic">No notes for this lead.</p> : (
              <div className="space-y-2" data-testid="lead-detail-notes">
                {noteLines.map((line, i) => (
                  <p key={i} className="text-sm leading-relaxed flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-primary shrink-0" /><span className="flex-1">{line}</span></p>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Activity Timeline</p>
              <Button size="sm" variant="outline" onClick={() => setLogOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" />Log</Button>
            </div>
            {activities.length === 0 ? <p className="text-sm text-muted-foreground italic">No activities yet. Log the first touchpoint.</p> : (
              <div className="space-y-2" data-testid="lead-detail-timeline">
                {activities.map((a) => (
                  <div key={a.id} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <Badge className={`${stageStyle[a.type] || "bg-zinc-500/12 text-zinc-400"} border-0 text-xs`}>{a.type}</Badge>
                      <span className="text-xs text-muted-foreground">{fmt(a.created_at)}</span>
                    </div>
                    {a.notes && <p className="text-sm mt-2 break-words">{a.notes}</p>}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span>{a.assignee_name}</span>
                      {a.follow_up_date && !a.follow_up_done && <span className="text-primary">Follow-up {fmtDate(a.follow_up_date)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <LogActivityDialog open={logOpen} onOpenChange={setLogOpen} presetLead={{ id: lead.id, name: lead.name }} onSaved={() => qc.invalidateQueries({ queryKey: ["activities", id] })} />
    </div>
  );
}
